import type { Component } from 'svelte';
import type { ClassValue, SVGAttributes } from 'svelte/elements';
import * as MDI from '@mdi/js';

import MLIcons from './ml';

export type SimpleIconPath = string;

export interface CustomIcon {
  path: string;
  opacity?: number | undefined;
}

export type IconPath = SimpleIconPath | CustomIcon[];

/**
 * Keys should match MDI name
 * e.g. 'account-multiple' for MDI.mdiAccountMultiple
 */
export const IconPathsByName: Record<
  string,
  IconPath | Component<SVGAttributes<SVGElement>>
> = {
  'account-plus-outline': MDI.mdiAccountPlusOutline,
  'account-group-outline': MDI.mdiAccountGroupOutline,
  'account-multiple': MDI.mdiAccountMultiple,
  'alert-circle-outline': MDI.mdiAlertCircleOutline,
  'alert-circle': MDI.mdiAlertCircle,
  'alert-outline': MDI.mdiAlertOutline,
  alert: MDI.mdiAlert,
  'ask-ai':
    'M20.7125 8.12786L20.4665 8.69386C20.4281 8.78603 20.3632 8.86476 20.2801 8.92013C20.197 8.97551 20.0994 9.00506 19.9995 9.00506C19.8996 9.00506 19.802 8.97551 19.7189 8.92013C19.6358 8.86476 19.571 8.78603 19.5325 8.69386L19.2865 8.12786C18.854 7.12658 18.0619 6.32378 17.0665 5.87786L16.3075 5.53886C16.2154 5.4965 16.1374 5.42863 16.0827 5.34329C16.0281 5.25795 15.999 5.15872 15.999 5.05736C15.999 4.956 16.0281 4.85677 16.0827 4.77143C16.1374 4.68609 16.2154 4.61822 16.3075 4.57586L17.0245 4.25686C18.0449 3.79824 18.8506 2.96574 19.2755 1.93086L19.5285 1.31986C19.5657 1.22521 19.6305 1.14396 19.7145 1.08669C19.7985 1.02941 19.8978 0.998779 19.9995 0.998779C20.1012 0.998779 20.2005 1.02941 20.2845 1.08669C20.3685 1.14396 20.4334 1.22521 20.4705 1.31986L20.7235 1.92986C21.148 2.96494 21.9533 3.79779 22.9735 4.25686L23.6915 4.57686C23.7833 4.61934 23.861 4.6872 23.9155 4.77243C23.97 4.85767 23.9989 4.95671 23.9989 5.05786C23.9989 5.15901 23.97 5.25805 23.9155 5.34329C23.861 5.42852 23.7833 5.49638 23.6915 5.53886L22.9315 5.87686C21.9363 6.32323 21.1446 7.12639 20.7125 8.12786ZM9.99951 2.99986H13.9995V4.99986H9.99951C8.40821 4.99986 6.88209 5.632 5.75687 6.75722C4.63165 7.88244 3.99951 9.40856 3.99951 10.9999C3.99951 14.6099 6.46151 16.9659 11.9995 19.4799V16.9999H13.9995C15.5908 16.9999 17.1169 16.3677 18.2422 15.2425C19.3674 14.1173 19.9995 12.5912 19.9995 10.9999H21.9995C21.9995 13.1216 21.1567 15.1564 19.6564 16.6567C18.1561 18.157 16.1212 18.9999 13.9995 18.9999V22.4999C8.99951 20.4999 1.99951 17.4999 1.99951 10.9999C1.99951 8.87813 2.84237 6.8433 4.34266 5.34301C5.84295 3.84271 7.87778 2.99986 9.99951 2.99986Z',
  apple: MDI.mdiApple,
  'application-outline': MDI.mdiApplicationOutline,
  'apple-keyboard-command': MDI.mdiAppleKeyboardCommand,
  'arrow-collapse-left': MDI.mdiArrowCollapseLeft,
  'arrow-collapse-right': MDI.mdiArrowCollapseRight,
  'arrow-expand-all': MDI.mdiArrowExpandAll,
  'arrow-expand-left': MDI.mdiArrowExpandLeft,
  'arrow-expand-right': MDI.mdiArrowExpandRight,
  'arrow-left': MDI.mdiArrowLeft,
  'arrow-right': MDI.mdiArrowRight,
  'arrow-top-right': MDI.mdiArrowTopRight,
  'arrow-top-right-bottom-left': MDI.mdiArrowTopRightBottomLeft,
  'arrow-collapse': MDI.mdiArrowCollapse,
  'arrow-up': MDI.mdiArrowUp,
  article: MDI.mdiTextBoxOutline,
  'auto-renew': MDI.mdiAutorenew,
  'axis-arrow': MDI.mdiAxisArrow,
  'backup-restore': MDI.mdiBackupRestore,
  block: MDI.mdiBlockHelper,
  'book-open': MDI.mdiBookOpenVariantOutline,
  'bookmark-outline': MDI.mdiBookmarkOutline,
  'broadcast-off': MDI.mdiBroadcastOff,
  bug: MDI.mdiBug,
  'bug-outline': MDI.mdiBugOutline,
  broadcast: MDI.mdiBroadcast,
  'calendar-blank': MDI.mdiCalendarBlank,
  'card-text-outline': MDI.mdiCardTextOutline,
  'camera-flip-outline': MDI.mdiCameraFlipOutline,
  'camera-outline': MDI.mdiCameraOutline,
  cancel: MDI.mdiCancel,
  'chart-line': MDI.mdiChartLine,
  'check-circle': MDI.mdiCheckCircle,
  'check-circle-outline': MDI.mdiCheckCircleOutline,
  'check-decagram': MDI.mdiCheckDecagram,
  check: MDI.mdiCheck,
  'checkbox-blank-outline': MDI.mdiCheckboxBlankOutline,
  'checkbox-indeterminate':
    'M5 3H19C20.1 3 21 3.89 21 5V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 3.89 3.89 3 5 3ZM7 13V11H17V13H7Z',
  'checkbox-marked': MDI.mdiCheckboxMarked,
  'chevron-double-up': MDI.mdiChevronDoubleUp,
  'chevron-down': MDI.mdiChevronDown,
  'chevron-left': MDI.mdiChevronLeft,
  'chevron-right': MDI.mdiChevronRight,
  'chevron-up': MDI.mdiChevronUp,
  'circle-md': MDI.mdiCircleMedium,
  'clock-outline': MDI.mdiClockOutline,
  close: MDI.mdiClose,
  'close-circle-outline': MDI.mdiCloseCircleOutline,
  'cloud-outline': MDI.mdiCloudOutline,
  'code-braces': MDI.mdiCodeBraces,
  'code-json': MDI.mdiCodeJson,
  'code-tags': MDI.mdiCodeTags,
  cog: MDI.mdiCog,
  console: MDI.mdiConsole,
  'content-copy': MDI.mdiContentCopy,
  'content-paste': MDI.mdiContentPaste,
  'content-duplicate': MDI.mdiContentDuplicate,
  'content-save-outline': MDI.mdiContentSaveOutline,
  'credit-card-outline': MDI.mdiCreditCardOutline,
  'crop-free': MDI.mdiCropFree,
  'cursor-move':
    'M13 6v5h5V7.7l4.3 4.3-4.3 4.3V13h-5v5h3.3L12 22.3 7.7 18H11v-5H6v3.3L1.7 12 6 7.7V11h5V6H7.7L12 1.7 16.3 6H13Z',
  'database-outline': MDI.mdiDatabaseOutline,
  'dock-left':
    'M5 21C4.45 21 3.97917 20.8042 3.5875 20.4125C3.19583 20.0208 3 19.55 3 19V5C3 4.45 3.19583 3.97917 3.5875 3.5875C3.97917 3.19583 4.45 3 5 3H19C19.55 3 20.0208 3.19583 20.4125 3.5875C20.8042 3.97917 21 4.45 21 5V19C21 19.55 20.8042 20.0208 20.4125 20.4125C20.0208 20.8042 19.55 21 19 21H5ZM8 19V5H5V19H8ZM10 19H19V5H10V19Z',
  'dock-right':
    'M19 21C19.55 21 20.0208 20.8042 20.4125 20.4125C20.8042 20.0208 21 19.55 21 19V5C21 4.45 20.8042 3.97917 20.4125 3.5875C20.0208 3.19583 19.55 3 19 3H5C4.45 3 3.97917 3.19583 3.5875 3.5875C3.19583 3.97917 3 4.45 3 5V19C3 19.55 3.19583 20.0208 3.5875 20.4125C3.97917 20.8042 4.45 21 5 21H19ZM16 19V5H19V19H16ZM14 19H5V5H14V19Z',
  domain: MDI.mdiDomain,
  'dots-horizontal': MDI.mdiDotsHorizontal,
  download: MDI.mdiTrayArrowDown,
  drag: MDI.mdiDrag,
  earth: MDI.mdiEarth,
  'file-outline': MDI.mdiFileOutline,
  folder: MDI.mdiFolderOutline,
  'lightning-bolt-outline': MDI.mdiLightningBoltOutline,
  export: MDI.mdiTrayArrowUp,
  'external-arrow':
    'M12 3C13.2333 3 14.3958 3.2375 15.4875 3.7125C16.5792 4.1875 17.5333 4.83333 18.35 5.65C19.1667 6.46667 19.8125 7.42083 20.2875 8.5125C20.7625 9.60417 21 10.7667 21 12C21 13.2333 20.7625 14.3958 20.2875 15.4875C19.8125 16.5792 19.1667 17.5333 18.35 18.35C17.5333 19.1667 16.5792 19.8125 15.4875 20.2875C14.3958 20.7625 13.2333 21 12 21C11.3167 21 10.6583 20.925 10.025 20.775C9.39167 20.625 8.75833 20.4083 8.125 20.125L9.65 18.6C10.0333 18.7333 10.4208 18.8333 10.8125 18.9C11.2042 18.9667 11.6 19 12 19C13.9333 19 15.5833 18.3167 16.95 16.95C18.3167 15.5833 19 13.9333 19 12C19 10.0667 18.3167 8.41667 16.95 7.05C15.5833 5.68333 13.9333 5 12 5C10.0667 5 8.41667 5.68333 7.05 7.05C5.68333 8.41667 5 10.0667 5 12C5 12.4 5.03333 12.7958 5.1 13.1875C5.16667 13.5792 5.26667 13.9667 5.4 14.35L3.9 15.85C3.6 15.25 3.375 14.6292 3.225 13.9875C3.075 13.3458 3 12.6833 3 12C3 10.7667 3.2375 9.60417 3.7125 8.5125C4.1875 7.42083 4.83333 6.46667 5.65 5.65C6.46667 4.83333 7.42083 4.1875 8.5125 3.7125C9.60417 3.2375 10.7667 3 12 3ZM13 16V12.4L4.4 21L3 19.6L11.6 11H8V9H15V16H13Z',
  'eye-outline': MDI.mdiEyeOutline,
  'eye-off-outline': MDI.mdiEyeOffOutline,
  'file-chart-outline': MDI.mdiFileChartOutline,
  'file-download-outline': MDI.mdiFileDownloadOutline,
  'file-swap-outline': MDI.mdiFileSwapOutline,
  fire: MDI.mdiFire,
  'filter-outline': [
    {
      path: 'M10.7,20.8c-.4,0-.7-.1-.9-.4s-.4-.6-.4-.9.1-.7.4-.9c.3-.3.6-.4.9-.4h2.7c.4,0,.7.1,1,.4s.4.6.4.9-.1.7-.4,1c-.3.3-.6.4-.9.4h-2.7ZM5.3,14.1c-.4,0-.7-.1-.9-.4-.3-.3-.4-.6-.4-.9s.1-.7.4-.9.6-.4.9-.4h13.3c.4,0,.7.1,1,.4.3.3.4.6.4.9s-.1.7-.4,1c-.3.3-.6.4-.9.4H5.3ZM1.3,7.4c-.4,0-.7-.1-.9-.4C.1,6.8,0,6.5,0,6.1c0-.4.1-.7.4-.9.3-.3.6-.4.9-.4h21.3c.4,0,.7.1,1,.4.3.3.4.6.4.9s-.1.7-.4,1c-.3.3-.6.4-.9.4H1.3Z',
    },
  ],
  'filter-sliders': MDI.mdiTuneVariant,
  gamepad: MDI.mdiGamepad,
  github: MDI.mdiGithub,
  'grid-orthographic':
    'M11 11V4H4v7h7Zm0 2H4v7h7v-7Zm2 7v-7h7v7h-7ZM3 22l-1-1V3l1-1h18l1 1v18l-1 1H3Zm17-11h-7V4h7v7Z',
  'grid-perspective':
    'M11 11V4.5H6.7l-1 6.5H11Zm0 2H5.4l-1 6.5H11V13Zm2 6.5V13h5.6l1 6.5H13Zm-10.1 2a.7.7 0 0 1-.8-.9L5 3.1c0-.3.4-.6.7-.6h12.8c.3 0 .6.3.7.6L22 20.6c0 .5-.3.9-.8.9H3ZM18.3 11H13V4.5h4.3l1 6.5Z',
  'help-circle-outline': MDI.mdiHelpCircleOutline,
  history: MDI.mdiHistory,
  'image-filter-center-focus': MDI.mdiImageFilterCenterFocus,
  'image-classification': MLIcons.ImageClassification,
  'information-outline': MDI.mdiInformationOutline,
  inbox: MDI.mdiInboxOutline,
  information: MDI.mdiInformation,
  instagram: MDI.mdiInstagram,
  'keyboard-return': MDI.mdiKeyboardReturn,
  'lan-connect': MDI.mdiLanConnect,
  laptop: MDI.mdiLaptop,
  'language-cpp':
    'M20.66 7a1.5 1.5 0 0 0-.55-.57l-7.34-4.24a1.67 1.67 0 0 0-1.54 0L3.89 6.43a1.68 1.68 0 0 0-.77 1.33v8.48a1.6 1.6 0 0 0 .22.76a1.5 1.5 0 0 0 .55.57l7.34 4.24a1.67 1.67 0 0 0 1.54 0l7.34-4.24a1.5 1.5 0 0 0 .55-.57a1.6 1.6 0 0 0 .22-.76V7.76a1.6 1.6 0 0 0-.22-.76M12 17.92A5.92 5.92 0 1 1 17.13 9L16 9.71l-.36.2l-1 .61A3 3 0 0 0 9 12a2.9 2.9 0 0 0 .4 1.48a3 3 0 0 0 5.13 0l2.6 1.52A5.94 5.94 0 0 1 12 17.92m5.92-5.59h-.66V13h-.65v-.66H16v-.66h.66V11h.65v.66h.66zm2.47 0h-.66V13h-.66v-.66h-.65v-.66h.65V11h.66v.66h.66z',
  'language-go': MDI.mdiLanguageGo,
  'language-python': MDI.mdiLanguagePython,
  'language-typescript': MDI.mdiLanguageTypescript,
  'layers-triple-outline': MDI.mdiLayersTripleOutline,
  link: MDI.mdiLink,
  linkedin: MDI.mdiLinkedin,
  'link-variant': MDI.mdiLinkVariant,
  linux: MDI.mdiLinux,
  lock: MDI.mdiLockOutline,
  logout: MDI.mdiLogout,
  maintenance:
    'M18.85 21.975C18.7167 21.975 18.5917 21.9542 18.475 21.9125C18.3583 21.8708 18.25 21.8 18.15 21.7L13.05 16.6C12.95 16.5 12.8792 16.3917 12.8375 16.275C12.7958 16.1583 12.775 16.0333 12.775 15.9C12.775 15.7667 12.7958 15.6417 12.8375 15.525C12.8792 15.4083 12.95 15.3 13.05 15.2L15.175 13.075C15.275 12.975 15.3833 12.9042 15.5 12.8625C15.6167 12.8208 15.7417 12.8 15.875 12.8C16.0083 12.8 16.1333 12.8208 16.25 12.8625C16.3667 12.9042 16.475 12.975 16.575 13.075L21.675 18.175C21.775 18.275 21.8458 18.3833 21.8875 18.5C21.9292 18.6167 21.95 18.7417 21.95 18.875C21.95 19.0083 21.9292 19.1333 21.8875 19.25C21.8458 19.3667 21.775 19.475 21.675 19.575L19.55 21.7C19.45 21.8 19.3417 21.8708 19.225 21.9125C19.1083 21.9542 18.9833 21.975 18.85 21.975ZM18.85 19.6L19.575 18.875L15.9 15.2L15.175 15.925L18.85 19.6ZM5.125 22C4.99167 22 4.8625 21.975 4.7375 21.925C4.6125 21.875 4.5 21.8 4.4 21.7L2.3 19.6C2.2 19.5 2.125 19.3875 2.075 19.2625C2.025 19.1375 2 19.0083 2 18.875C2 18.7417 2.025 18.6167 2.075 18.5C2.125 18.3833 2.2 18.275 2.3 18.175L7.6 12.875H9.725L10.575 12.025L6.45 7.9H5.025L2 4.875L4.825 2.05L7.85 5.075V6.5L11.975 10.625L14.875 7.725L13.8 6.65L15.2 5.25H12.375L11.675 4.55L15.225 1L15.925 1.7V4.525L17.325 3.125L20.875 6.675C21.1583 6.95833 21.375 7.27917 21.525 7.6375C21.675 7.99583 21.75 8.375 21.75 8.775C21.75 9.175 21.675 9.55833 21.525 9.925C21.375 10.2917 21.1583 10.6167 20.875 10.9L18.75 8.775L17.35 10.175L16.3 9.125L11.125 14.3V16.4L5.825 21.7C5.725 21.8 5.61667 21.875 5.5 21.925C5.38333 21.975 5.25833 22 5.125 22ZM5.125 19.6L9.375 15.35V14.625H8.65L4.4 18.875L5.125 19.6ZM5.125 19.6L4.4 18.875L4.775 19.225L5.125 19.6Z',
  magnet:
    'M3 17v-6a9 9 0 1 1 18 0v6h-4v-6a5 5 0 0 0-10 0v6m10 2h4v3h-4M3 19h4v3H3',
  'magnet-off':
    'M21 16.8V11A9 9 0 0 0 7.2 3.4l3 3A5 5 0 0 1 17 11v2l4 3.8ZM3 19h4v3H3v-3Z M3 11v6h4v-6c0-.6.1-1.3.4-1.9L17.5 19H17v3h3.6l2 1.8 1.3-1.4-2.9-2.8-.6-.6-2-2-1.4-1.3-8.5-8.3-2.9-2.7L2 1 .5 2.5l3.9 3.7A9 9 0 0 0 3 11Z',
  magnify: MDI.mdiMagnify,
  'manage-history':
    'M2.9826 9.86426V3.86426H4.9826V6.21426C5.83261 5.14759 6.87011 4.32259 8.09511 3.73926C9.32011 3.15592 10.6159 2.86426 11.9826 2.86426C14.4826 2.86426 16.6076 3.73926 18.3576 5.48926C20.1076 7.23926 20.9826 9.36426 20.9826 11.8643H18.9826C18.9826 9.91426 18.3034 8.26009 16.9451 6.90176C15.5868 5.54342 13.9326 4.86426 11.9826 4.86426C10.8326 4.86426 9.75761 5.13092 8.75761 5.66426C7.75761 6.19759 6.91594 6.93092 6.2326 7.86426H8.9826V9.86426H2.9826ZM3.0326 12.8643H5.08261C5.28261 14.4143 5.92011 15.7268 6.99511 16.8018C8.07011 17.8768 9.3576 18.5309 10.8576 18.7643L12.0576 20.8643C9.7576 20.8643 7.74094 20.1018 6.00761 18.5768C4.27427 17.0518 3.2826 15.1476 3.0326 12.8643ZM13.3326 14.6143L10.9826 12.2643V6.86426H12.9826V11.4643L14.3826 12.8643L13.3326 14.6143ZM17.9576 23.8643L17.6576 22.3643C17.4576 22.2809 17.2701 22.1934 17.0951 22.1018C16.9201 22.0101 16.7409 21.8976 16.5576 21.7643L15.1076 22.2143L14.1076 20.5143L15.2576 19.5143C15.2243 19.2976 15.2076 19.0809 15.2076 18.8643C15.2076 18.6476 15.2243 18.4309 15.2576 18.2143L14.1076 17.2143L15.1076 15.5143L16.5576 15.9643C16.7409 15.8309 16.9201 15.7184 17.0951 15.6268C17.2701 15.5351 17.4576 15.4476 17.6576 15.3643L17.9576 13.8643H19.9576L20.2576 15.3643C20.4576 15.4476 20.6493 15.5434 20.8326 15.6518C21.0159 15.7601 21.1909 15.8809 21.3576 16.0143L22.8076 15.5143L23.8076 17.2643L22.6576 18.2643C22.6909 18.4809 22.7076 18.6893 22.7076 18.8893C22.7076 19.0893 22.6909 19.2976 22.6576 19.5143L23.8076 20.5143L22.8076 22.2143L21.3576 21.7643C21.1743 21.8976 20.9951 22.0101 20.8201 22.1018C20.6451 22.1934 20.4576 22.2809 20.2576 22.3643L19.9576 23.8643H17.9576ZM18.9576 20.8643C19.5076 20.8643 19.9784 20.6684 20.3701 20.2768C20.7618 19.8851 20.9576 19.4143 20.9576 18.8643C20.9576 18.3143 20.7618 17.8434 20.3701 17.4518C19.9784 17.0601 19.5076 16.8643 18.9576 16.8643C18.4076 16.8643 17.9368 17.0601 17.5451 17.4518C17.1534 17.8434 16.9576 18.3143 16.9576 18.8643C16.9576 19.4143 17.1534 19.8851 17.5451 20.2768C17.9368 20.6684 18.4076 20.8643 18.9576 20.8643Z',
  markdown: MDI.mdiLanguageMarkdownOutline,
  megaphone: MDI.mdiBullhornVariantOutline,
  memory: MDI.mdiMemory,
  menu: MDI.mdiMenu,
  merge: MDI.mdiMerge,
  'message-alert-outline': MDI.mdiMessageAlertOutline,
  minus: MDI.mdiMinus,
  'monitor-dashboard': MDI.mdiMonitorDashboard,
  'multiplication-box': MDI.mdiMultiplicationBox,
  'navigation-variant-outline': MDI.mdiNavigationVariantOutline,
  'navigation-variant': MDI.mdiNavigationVariant,
  'network-outline': MDI.mdiNetworkOutline,
  'note-outline': MDI.mdiNoteOutline,
  'object-detection': MLIcons.ObjectDetection,
  onnx: MLIcons.Onnx,
  'open-in-new': MDI.mdiOpenInNew,
  'package-variant-closed': MDI.mdiPackageVariantClosed,
  'package-variant-closed-check': MDI.mdiPackageVariantClosedCheck,
  pause: MDI.mdiPause,
  'pause-circle-outline': MDI.mdiPauseCircleOutline,
  'pencil-outline': MDI.mdiPencilOutline,
  'picture-in-picture-top-right': MDI.mdiPictureInPictureTopRight,
  'play-circle-outline': MDI.mdiPlayCircleOutline,
  plus: MDI.mdiPlus,
  pytorch: MLIcons.PyTorch,
  'radiobox-blank': MDI.mdiRadioboxBlank,
  'radiobox-indeterminate-variant': MDI.mdiRadioboxIndeterminateVariant,
  'radiobox-marked': MDI.mdiRadioboxMarked,
  pulse:
    'M12 22L9 7.95L6.8 18H2V16H5.2L8.025 3H10L13.05 17.3L15 9H17L18.8 16H22V18H17.25L16.05 13.3L14 22H12Z',
  qrcode: MDI.mdiQrcode,
  refresh: MDI.mdiRefresh,
  resize:
    'm11 12 4-4h-4V6h7v7h-2V9l-4 4v3h8V4H8v8h3ZM22 2v16H12v4H2V12h4V2h16ZM10 14H4v6h6v-6Z',
  'robot-industrial-outline': MDI.mdiRobotIndustrialOutline,
  'robot-outline': MDI.mdiRobotOutline,
  'rocket-launch-outline': MDI.mdiRocketLaunchOutline,
  'script-text-outline': MDI.mdiScriptTextOutline,
  'selection-ellipse': MDI.mdiSelectionEllipse,
  'selection-drag': MDI.mdiSelectionDrag,
  send: MDI.mdiSend,
  'shield-key-outline': MDI.mdiShieldKeyOutline,
  'source-merge': MDI.mdiSourceMerge,
  sparkles:
    'M10.6 9.6L9 15L7.4 9.6L2 8L7.4 6.4L9 1L10.6 6.4L16 8L10.6 9.6ZM17 14.2L21 12L18.8 16L21 20L17 17.8L13 20L15.2 16L13 12L17 14.2ZM10 16L8.3 19L10 22L7 20.3L4 22L5.7 19L4 16L7 17.7L10 16Z',
  stop: MDI.mdiStop,
  'stop-circle-outline': MDI.mdiStopCircleOutline,
  'subdirectory-arrow-right': MDI.mdiSubdirectoryArrowRight,
  sync: MDI.mdiSync,
  table: MDI.mdiTable,
  'tag-outline': MDI.mdiTagOutline,
  tensorflow: MLIcons.TensorFlow,
  'text-search': MDI.mdiTextSearch,
  'thumb-down': MDI.mdiThumbDown,
  'thumb-up': MDI.mdiThumbUp,
  tools: MDI.mdiTools,
  'trash-can-outline': MDI.mdiTrashCanOutline,
  'toggle-switch': MDI.mdiToggleSwitch,
  'toggle-switch-off': MDI.mdiToggleSwitchOff,
  'toggle-switch-off-outline': MDI.mdiToggleSwitchOffOutline,
  'toggle-switch-outline': MDI.mdiToggleSwitchOutline,
  twitter: MDI.mdiTwitter,
  undo: MDI.mdiUndo,
  'unfold-more-vertical': MDI.mdiUnfoldMoreVertical,
  'unfold-less-horizontal': MDI.mdiUnfoldLessHorizontal,
  'unfold-more-horizontal': MDI.mdiUnfoldMoreHorizontal,
  upload: MDI.mdiUpload,
  'viam-component':
    'm12 4-8 8 8 8 8-8-8-8Zm.5-2.4a.8.8 0 0 0-1 0l-9.9 9.9c-.3.3-.3.7 0 1l9.9 9.9c.3.3.7.3 1 0l9.9-9.9c.3-.3.3-.7 0-1l-9.9-9.9Z',
  'viam-flutter':
    'M17 1.7h-5.2L3.5 10 6 12.6l2.3-2.3 8.6-8.6Zm-4.5 12.1 4.4-4.5H12l-.3.1-1.8 1.9-2.6 2.5 2.6 2.6 1.8 1.8s0 .2.2.1h5l-4.4-4.5Z',
  'viam-fragment':
    'M17 22v-2h3v-3h2v3.5c0 .4-.2.7-.5 1-.3.3-.6.5-1 .5H17ZM7 22H3.5c-.4 0-.7-.2-1-.5-.3-.3-.5-.6-.5-1V17h2v3h3v2ZM17 2h3.5c.4 0 .7.2 1 .5.3.3.5.6.5 1V7h-2V4h-3V2ZM7 2v2H4v3H2V3.5c0-.4.2-.7.5-1 .3-.3.6-.5 1-.5H7Zm6 15.3 4-2.4v-4.5l-4 2.3v4.6Zm-1-6.4 4-2.3-4-2.3-4 2.3 4 2.3Zm-5 4 4 2.3v-4.5l-4-2.3v4.5Zm11.2-7.3c.5.3.8.7.8 1.3v6.3c0 .6-.3 1-.8 1.4l-5.4 3.1c-.6.4-1 .4-1.6 0l-5.4-3.1c-.5-.4-.8-.8-.8-1.4V9c0-.6.3-1 .8-1.3l5.5-3.2a1.6 1.6 0 0 1 1.4 0l5.5 3.2Z',
  'viam-local-module':
    'M3 3v18h18V3H3Zm5.6 9.9H5V5h8v3.6H8.6V13Zm-3.6 6h14V5h-4v5.6h-4.4V15H5v4Z',
  'viam-mixed-part-status': [
    {
      path: 'M12 11c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2Zm6 2c0-3.31-2.69-6-6-6s-6 2.69-6 6c0 2.22 1.21 4.15 3 5.19l1-1.74c-1.19-.7-2-1.97-2-3.45 0-2.21 1.79-4 4-4s4 1.79 4 4c0 1.48-.81 2.75-2 3.45l1 1.74c1.79-1.04 3-2.97 3-5.19Z',
    },
    {
      path: 'M12 3C6.48 3 2 7.48 2 13c0 3.7 2.01 6.92 4.99 8.65l1-1.73C5.61 18.53 4 15.96 4 13c0-4.42 3.58-8 8-8s8 3.58 8 8c0 2.96-1.61 5.53-4 6.92l1 1.73c2.99-1.73 5-4.95 5-8.65 0-5.52-4.48-10-10-10Z',
      opacity: 0.5,
    },
  ],
  'viam-ml-model':
    'M22 4.3a2.3 2.3 0 0 1-3 2.2L16.2 10a2.6 2.6 0 0 1-.2 3l3.1 4.5a2.3 2.3 0 1 1-1.6 3h-11a2.3 2.3 0 1 1-1-2.8l6.1-5.1a2.6 2.6 0 0 1 0-2l-6-4.4a2 2 0 0 1-1.3.4 2.3 2.3 0 1 1 2.2-3.1h11a2.3 2.3 0 0 1 4.6.8ZM14 14c-.4 0-.8 0-1.2-.2l-6.2 5h11l.2-.4-3.2-4.4H14Zm0-5.2c-.6 0-1 .2-1.5.4L7 5.2h10.7l.1.3L15 9l-1-.2Z',
  'viam-process':
    'M9 14H14V15.7C13.9 15.8 13.9 15.9 13.8 16H9V14ZM9 12H14V10H9V12ZM9 8H14V6H9V8ZM7 5C7 4.4 7.4 4 8 4H16V13.8C16.6 13.4 17.3 13.2 18 13.1V5C18 4.4 18.4 4 19 4C19.6 4 20 4.4 20 5V6H22V5C22 3.3 20.7 2 19 2H8C6.3 2 5 3.3 5 5V16H7V5ZM13 19V18.4V18H2V19C2 20.7 3.3 22 5 22H13.8C13.3 21.1 13 20.1 13 19ZM17 16V22L22 19L17 16Z',
  'viam-registry-module': 'M3 21V3h18v18H3Zm2-2h14V5h-4.7v4.7H9.7v4.6H5V19Z',
  'viam-remote-part':
    'M4 6V4a12 12 0 0 1 12 12h-2A10 10 0 0 0 4 6Zm0 4V8a8 8 0 0 1 8 8h-2a6 6 0 0 0-6-6Zm0 2a4 4 0 0 1 4 4H4v-4Zm-1 6h16v-2l3 3-3 3v-2H3v-2Z',
  'viam-service': [
    {
      path: 'M12 3.95534L3.95534 12L12 20.0447L20.0447 12L12 3.95534ZM12.5303 1.62189C12.2374 1.329 11.7626 1.329 11.4697 1.62189L1.62189 11.4697C1.329 11.7626 1.329 12.2374 1.62189 12.5303L11.4697 22.3781C11.7626 22.671 12.2374 22.671 12.5303 22.3781L22.3781 12.5303C22.671 12.2374 22.671 11.7626 22.3781 11.4697L12.5303 1.62189Z',
    },
    {
      path: 'M13.0842 13.0842L12 16.7435L10.9158 13.0842L7.25649 12L10.9158 10.9157L12 7.25647L13.0842 10.9157L16.7435 12L13.0842 13.0842Z',
    },
  ],
  'video-outline': MDI.mdiVideoOutline,
  'view-agenda-outline':
    'M5 11C4.45 11 3.97917 10.8042 3.5875 10.4125C3.19583 10.0208 3 9.55 3 9V5C3 4.45 3.19583 3.97917 3.5875 3.5875C3.97917 3.19583 4.45 3 5 3H19C19.55 3 20.0208 3.19583 20.4125 3.5875C20.8042 3.97917 21 4.45 21 5V9C21 9.55 20.8042 10.0208 20.4125 10.4125C20.0208 10.8042 19.55 11 19 11H5ZM5 9H19V5H5V9ZM5 21C4.45 21 3.97917 20.8042 3.5875 20.4125C3.19583 20.0208 3 19.55 3 19V15C3 14.45 3.19583 13.9792 3.5875 13.5875C3.97917 13.1958 4.45 13 5 13H19C19.55 13 20.0208 13.1958 20.4125 13.5875C20.8042 13.9792 21 14.45 21 15V19C21 19.55 20.8042 20.0208 20.4125 20.4125C20.0208 20.8042 19.55 21 19 21H5ZM5 19H19V15H5V19Z',
  'view-dashboard-outline': MDI.mdiViewDashboardOutline,
  'format-list-bulleted': MDI.mdiFormatListBulleted,
  webcam: MDI.mdiWebcam,
  webhook: MDI.mdiWebhook,
  'widgets-outline': MDI.mdiWidgetsOutline,
  windows: MDI.mdiMicrosoftWindows,
  'wrench-outline': MDI.mdiWrenchOutline,
  'resource-deprecated': MDI.mdiCloseOctagonOutline,
} as const;

/**
 * The possible icon names that can be rendered. This is good for typing props.
 */
export type IconName = keyof typeof IconPathsByName;

export type Size = 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';

export interface IconProps extends Omit<SVGAttributes<SVGElement>, 'cx'> {
  /** The name of the icon. */
  name: IconName;
  /** The size of the icon. */
  size?: Size;
  /** Additional CSS classes to pass to the svg. */
  cx?: ClassValue;
}
